import type { BrandTheme, RTableProps } from '@ramp/components';

import type { NoticeDeliveryMethod } from 'api/notices';
import type { INotification } from 'utils/notifications';
import type {
  AndroidPendingUpdates,
  LinuxOs,
  WindowsSensorData,
  WindowsUpdateHistoryEntry,
  WindowsUpdateHistoryStateEntry
} from './api/devices';
import type { ShodanAttempt, TargetGroupLink } from './api/externalScans';
import type { FirmBasic } from './api/firms';
import type { DeviceMdmLink } from './api/mdm';
import type { Nullable } from './helpers';

export type Platforms = 'android' | 'ios' | 'ipados' | 'macos' | 'windows' | 'linux';

export type Severity = 'critical' | 'high' | 'medium' | 'low' | 'none';

export type Status =
  | 'active'
  | 'disabled'
  | 'pending'
  | 'archived'
  | 'email_verification'
  | 'password_reset'
  | 'password_reset_activation'
  | 'password_set_verification';

/** How a device scan was triggered — aligned with backend scan DTOs and `ScanInvokedTag`. */
export type ScanInvokedBy = 'user' | 'background' | 'automatic' | 'unknown';

export enum Role {
  none,
  user,
  admin,
  firmadmin,
  superadmin
}

export enum AppState {
  IN_VERIFICATION = 'in_verification',
  PLAYSTORE_FOUND = 'playstore_found',
  PLAYSTORE_TESTING = 'playstore_testing',
  NOT_FOUND = 'not_found',
  REDAMP_TESTING = 'redamp_testing',
  CATEGORIZED = 'categorized'
}

export enum AppCategory {
  SYSTEM = 'system',
  THIRD_PARTY = 'third_party',
  VENDOR_PREINSTALLED = 'vendor_preinstalled',
  MALICIOUS = 'malicious',
  REDAMP_VERIFIED = 'redamp_verified'
}

export interface BaseTableProps {
  enableUrlParams?: RTableProps<any>['enableUrlParams'];
  showGlobalFilters?: boolean;
  showCard?: RTableProps<any>['showCard'];
}

export interface LoginAsAdminProps {
  /**
   * The entity ID of the client to login as
   */
  clientEntityId: Nullable<string>;

  /**
   * The name of the client to login as
   */
  clientName: Nullable<string>;

  /**
   * The previous user to login as
   */
  previousUser: Nullable<User>;

  /**
   * The partner to login as
   */
  loginAsPartnerAdmin?: LoginAsPartnerAdminProps;

  /**
   * The callback to call after login
   */
  afterLoginCallback?: () => void;
}

export interface LoginAsPartnerAdminProps {
  /**
   * The entity ID of the partner to login as
   */
  partnerEntityId: Nullable<string>;

  /**
   * The name of the partner to login as
   */
  partnerName: Nullable<string>;

  /**
   * The previous user to login as
   */
  previousUser: Nullable<User>;
}

export type ClientStatus = 'active' | 'disabled' | 'archived';

export interface ClientLicenseChangelog {
  changed_license: {
    user_licenses: number;
    device_licenses: number;
    license_expiration_date: string;
  };
  updated_by: {
    id: string;
    name: string;
    email: string;
    role: Role;
  };
  comment: string;
  created: string;
}

export interface ClientLicense {
  license_expiration_date: string;
  user_licenses: number;
  device_licenses: number;
  used_user_licenses: number;
  used_device_licenses: number;
  changelog: ClientLicenseChangelog[];
}

export interface Client {
  id: string;
  name: string;
  created_at: string;
  firm_id: string;
  firm_name: string;
  status: ClientStatus;
  contact_name: string;
  contact_email: string;
  scan_frequency_in_seconds: number;
  license: ClientLicense;
  devices: FirmDevices;
  total_severity_count: SeverityCount;
  issues: FirmIssues;
  global_score: number;
  os_score: number;
  settings_score: number;
  application_score: number;
  device_score: number;
  country: string;
  street: string;
  city: string;
  zip_code: string;
  state: Nullable<string>;
  company_id: string;
  vat_id: Nullable<string>;
  is_approved: boolean;
  last_admin_login?: Nullable<string>;
}

export interface ClientDetails extends Client {
  managed_by_firm: FirmBasic;
  created_by_firm: FirmBasic;
  can_revoke_firm_management: boolean;
  notice_delivery_method: NoticeDeliveryMethod;
  receive_regular_quizzes: boolean;
  /** Set in Django admin only: the client's dashboard is the new one. */
  dashboard_pilot: boolean;
  /** Set in Django admin only: Greenbone surfaces are shown. */
  greenbone_enabled: boolean;
}

export interface PartnerAdmin {
  id: string;
  email: string;
  name: string;
  position: Nullable<string>;
  status: Status;
  registered: string;
  last_login: Nullable<string>;
  otp_enabled: boolean;
  role: Role;
}

export interface CreateClient {
  name: string;
  admin_email: string;
  admin_password: string;
  admin_password_confirmation: string;
  contact_email: string;
  contact_name: string;
  country: string;
  street: string;
  city: string;
  zip_code: string;
  language: string;
  state?: string;
  company_id: string;
  vat_id?: string;
  managed_by_firm: boolean;
}

export interface GeneralRecommendation {
  id: string;
  platform: Platforms[];
  type: 'do' | 'consider_doing' | 'avoid';
  title: string;
  description: string;
  importness: number;
}

export interface LocalRecommendation extends GeneralRecommendation {
  language_code: string;
  url: string;
  regions: string[] | 'GLOBAL';
  created: string;
}

export interface UserCredentials {
  email: string;
  password: string;
}

export interface DecodedToken extends User {
  token_type: string;
  exp: number;
  jti: string;
}

export interface JWT {
  access: string;
  refresh: string;
}

export interface TokenSchema {
  access: Nullable<string>;
  refresh: Nullable<string>;
  otp_token: Nullable<string>;
}

export interface User {
  user_id: string;
  entity_id: Nullable<string>;
  name: Nullable<string>;
  email: Nullable<string>;
  employer_name: Nullable<string>;
  role: number;
  read_only: boolean;
  position: Nullable<string>;
  otp_enabled: boolean;
  scan_frequency_in_seconds: Nullable<number>;
  theme: 'light' | 'dark' | 'all';
  logo_primary_dark: string;
  logo_primary_light: string;
  logo_secondary_dark: string;
  logo_secondary_light: string;
  color_scheme_light: BrandTheme;
  color_scheme_dark: BrandTheme;
  client_approved: boolean;
}

export interface NotificationsResult {
  received_device_ids: Nullable<string[]>;
  not_found_device_ids: Nullable<string[]>;
  devices_without_token: Nullable<Device[]>;
  archived_devices: Nullable<Device[]>;
  failed_send_attempts: Nullable<DeviceError[]>;
  successful_send_attempts: Nullable<Device[]>;
  successful_send_message_ids: Nullable<string[]>;
  notifications_send: Nullable<INotification>;
}

export interface TablePageSize {
  id: string;
  pageSize: number;
}

export interface UserData {
  id: string;
  email: string;
  name: string;
  first_name: Nullable<string>;
  last_name: Nullable<string>;
  position: Nullable<string>;
  status: Status;
  otp_enabled: boolean;
  registered: string;
  last_login: Nullable<string>;
  android_devices: number;
  ios_devices: number;
  ipados_devices: number;
  macos_devices: number;
  windows_devices: number;
  linux_devices: number;
  role: 0 | 1 | 2 | 3;
  country: string;
  language: string;
  department_id?: Nullable<string>;
  department_name?: Nullable<string>;
}

export interface Department {
  id: string;
  name: string;
}

export interface DepartmentCreate {
  name: string;
}

export interface DepartmentPatch {
  name?: Nullable<string>;
}

export interface BulkUsersUpdate {
  user_ids: string[];
  // Omit to leave unchanged; pass `null` to clear the department.
  department_id?: Nullable<string>;
  // Omit/`null` to leave unchanged.
  status?: Nullable<Status>;
}

export interface UserDataWithIssues extends UserData {
  issues: Issue[];
}

export interface AppIssue {
  issue_codename: string;
  issue_title: string;
  issue_description: string;
  kb_link: string;
  score: Nullable<number>;
  solution_description: string;
}

export interface AndroidApplicationsInfo {
  security_score: Nullable<number>;
  evaluated: Nullable<AppInfo[]>;
  issues: AppIssue[];
}

export interface Vulnerability {
  cve: string;
  cwe: string;
  score: number;
  description: string;
  published: Nullable<string>;
}

export interface Device {
  id: string;
  name: Nullable<string>;
  app_version: Nullable<string>;
  marketing_name: Nullable<string>;
  device: Nullable<string>;
  model: Nullable<string>;
  platform: Nullable<string>;
  manufacturer: Nullable<string>;
  build: Nullable<string>;
}

export interface DeviceError {
  device: Device;
  error: Nullable<string>;
}

export interface ProblematicApplication {
  package_name: string;
  security_score: number;
  version: Nullable<string>;
  update_date: number;
  maintained: boolean;
  malicious: boolean;
  installed_from: Nullable<string>;
  analysis_score: Nullable<number>;
  application_name: Nullable<string>;
}

export interface AppUpdate {
  updated: boolean;
  maintained: boolean;
  latest_available_version: string;
  latest_available_update_timestamp: number;
}

export interface AppAnalysis {
  security_score: number;
  trackers_detection: number;
  virustotal_detection: number;
}

export interface AppInfo {
  security_score: number;
  package_name: string;
  application_name: Nullable<string>;
  eval_category: 'risky' | 'in_verification' | 'verified' | 'system';
  update: Nullable<AppUpdate>;
  analysis: Nullable<AppAnalysis>;
  issues: string[];
  details: string[];
}

export interface EvaluatedApplication {
  security_score: number;
  package_name: string;
  latest_available_version: string;
  latest_available_update_timestamp: number;
  maintained: boolean;
  updated: boolean;
  mars_state: string;
  categories: string[];
  installed_from: Nullable<string>;
  analysis_score: Nullable<number>;
  application_name: Nullable<string>;
}

export interface NotEvaluatedApplication {
  package_name: string;
  security_score: number;
  version: Nullable<string>;
  install_timestamp: number;
  update_timestamp: number;
  installed_from: Nullable<string>;
  mars_state: string;
  categories: string[];
  analysis_score: Nullable<number>;
  application_name: Nullable<string>;
}

export interface DeviceApps {
  evaluated_applications: EvaluatedApplication[];
  not_evaluated_applications: NotEvaluatedApplication[];
}

export interface ProcessedAppInfo {
  problematic: ProblematicApplication[];
  third_party: NotEvaluatedApplication[];
  verified: EvaluatedApplication[];
  system: NotEvaluatedApplication[];
  in_verification: NotEvaluatedApplication[];
  unupdated: EvaluatedApplication[];
  updated_count: number;
  unupdated_count: number;
}

export type DeviceStatus = 'active' | 'disabled' | 'pending' | 'archived';

export interface WindowsUserSession {
  session_id: string;
  username: string;
  state: 'active' | 'locked' | 'disconnected';
}

export interface BasePlatformSpecificData {
  platform: 'macos' | 'ios' | 'ipados' | 'android' | 'windows';
  os_name?: string;
  os_version?: string;
  os_patch?: string;
  os_build?: string;
}

export interface WindowsPlatformSpecificData {
  platform: 'windows';
  os_name: string;
  os_edition: string;
  user_sessions: WindowsUserSession[];
}

export interface AndroidPlatformSpecificData extends BasePlatformSpecificData {
  platform: 'android';
  os_firmware?: string;
}

/** Subset of `LinuxOs` carried on `/devices` list rows (`platform_specific_data`). */
export type LinuxPlatformDistroFields = Pick<LinuxOs, 'distro_id' | 'distro_codename' | 'pretty_name' | 'version'>;

export interface LinuxPlatformSpecificData extends Partial<LinuxPlatformDistroFields> {
  platform: 'linux';
  os_name?: string;
  os_version?: string;
}

export interface DeviceEvaluation {
  id: string;
  latest_available_app_version: Nullable<string>;
  created: string;
  invoked_by: Nullable<'user' | 'automatic'>;
  supported: Nullable<boolean>;
  hostname: Nullable<string>;
  global_score: number;
  device_score: Nullable<number>;
  applications_score: Nullable<number>;
  privacy_score: Nullable<number>;
  network_score: Nullable<number>;
  name: Nullable<string>;
  app_version: Nullable<string>;
  marketing_name: string;
  device: string;
  model: string;
  platform: string;
  manufacturer: string;
  build: Nullable<string>;
  status: DeviceStatus;
  device_supported: Nullable<boolean>;
  online: boolean;
  last_online: string;
  platform_specific_data?: Nullable<BasePlatformSpecificData | WindowsPlatformSpecificData | LinuxPlatformSpecificData>;
  sensor_data?: Nullable<WindowsSensorData>;
}

export interface DeviceUserEvaluation extends DeviceEvaluation {
  user_id: Nullable<string>;
  user_email: Nullable<string>;
  user_name: Nullable<string>;
  mdm?: DeviceMdmLink;
  new_user?: {
    id: string;
    email: string;
    name: string;
    role: Role;
  };
}

export interface DeviceScore {
  marketing_name: string;
  name: string;
  model: string;
  platform: string;
  manufacturer: string;
  device: string;
  end_of_life?: Nullable<string>;
  status: DeviceStatus;
  recommendations: DeviceRecommendation[];
}

export interface DeviceSettings {
  security_score: Nullable<number>;
  security_system: Nullable<'none' | 'pin' | 'fingerprint' | 'face_id'>;
  rooted: Nullable<boolean>;
  jailbreak?: Nullable<boolean>;
  nfc_status: Nullable<boolean>;
  bluetooth_status: Nullable<boolean>;
  usb_debug_status: Nullable<boolean>;
  firewall?: Nullable<boolean>;
  antivirus?: Nullable<{
    installed_antivirus: Nullable<{ display_name: string }[]>;
    is_enabled: Nullable<boolean>;
    security_score: Nullable<number>;
  }>;
  device_settings?: Nullable<{
    bitlocker: Nullable<boolean>;
    has_password: Nullable<boolean>;
    security_score: Nullable<number>;
  }>;
  face_id: Nullable<boolean>;
  retina_auth: Nullable<boolean>;
  secured_by_pin: Nullable<boolean>;
  fingerprint: Nullable<boolean>;
  recommendations: DeviceRecommendation[];
}

export interface DevicesStats {
  ios: PlatformStats;
  android: PlatformStats;
  windows: PlatformStats;
  ipados: PlatformStats;
  macos: PlatformStats;
  linux: PlatformStats;
}

export interface Dns {
  hostname: string;
}

export interface GlobalScore {
  security_score: number;
  device_score: number;
  privacy_score: Nullable<number>;
  network_score: Nullable<number>;
  apps_score: Nullable<number>;
  platform_score: Nullable<number>;
  os_settings_score: Nullable<number>;
  network_settings_score: Nullable<number>;
  network_traffic_score: Nullable<number>;
}

export interface IssueStats {
  current_severity_count: TotalSeverityCount;
  severity_counts: SeverityCount[];
}

export interface ShodanPortResult {
  port: number;
  timestamp: string;
  data?: Nullable<string>;
  transport: 'tcp' | 'udp';
  product?: Nullable<string>;
}

export interface Shodan80Result extends ShodanPortResult {
  ip: Nullable<number>;
  os: Nullable<string>;
  asn: Nullable<string>;
  cpe: Nullable<string[]>;
  isp: Nullable<string>;
  org: Nullable<string>;
  data: Nullable<string>;
  hash: Nullable<number>;
  http: Nullable<{
    host: Nullable<string>;
    html: Nullable<string>;
    title: Nullable<string>;
    robots: Nullable<string>;
    server: Nullable<string>;
    status: Nullable<number>;
    sitemap: Nullable<string>;
    securitytxt: Nullable<string>;
    location: Nullable<string>;
  }>;
  opts: Nullable<object>;
  cpe23: Nullable<string[]>;
  ip_str: Nullable<string>;
  domains: Nullable<string[]>;
}

export interface Shodan443Result extends Shodan80Result {
  ssl: Nullable<{
    alpn: Nullable<string[]>;
    cert: Nullable<{
      issued: Nullable<string>;
      issuer: Nullable<{
        C: string;
        O: string;
        CN: string;
      }>;
      expired: Nullable<boolean>;
      expires: Nullable<string>;
    }>;
    cipher: Nullable<{
      version: Nullable<string>;
      bits: Nullable<number>;
      name: Nullable<string>;
    }>;
    versions: Nullable<string[]>;
  }>;
  opts: Nullable<{
    vulns: Nullable<object>;
    heartbleed: Nullable<string>;
  }>;
}

export interface ShodanResult {
  area_code: Nullable<any>;
  asn: Nullable<string>;
  city: Nullable<string>;
  country_code: Nullable<string>;
  country_name: Nullable<string>;
  data: Nullable<ShodanPortResult[]>;
  domains: Nullable<string[]>;
  hostnames: Nullable<string[]>;
  ip: Nullable<number>;
  ip_str: Nullable<string>;
  isp: Nullable<string>;
  last_update: Nullable<string>;
  latitude: Nullable<number>;
  longitude: Nullable<number>;
  org: Nullable<string>;
  ports: Nullable<number[]>;
  region_code: Nullable<string>;
  tags: Nullable<string[]>;
}

export interface ExternalNetworkScan {
  created: string;
  public_ip: string;
  result_data: Nullable<ShodanResult>;
}

export interface CustomScanIP {
  created: string;
  ip_address: string;
  last_scan_result: Nullable<ExternalNetworkScan>;
}

export interface DeviceOpenPort {
  port: number;
  protocol: 'TCP' | 'UDP' | 'TCP/UDP';
}

export interface NetworkDeviceSnapshot {
  created: string;
  ip_address: Nullable<string>;
  name: Nullable<string>;
  tags: string[];
  open_ports: DeviceOpenPort[];
}

export interface NetworkDevice {
  mac_address: string;
  manufacturer: Nullable<string>;
  description: Nullable<string>;
  last_seen: string;
  snapshots: NetworkDeviceSnapshot[];
}

export interface NetworkInfo {
  security_score: Nullable<number>;
  latest_network_scan: NetworkScanInfo[];
  recommendations: DeviceRecommendation[];
}

export interface NetworkScanInfo {
  name: string;
  created: string;
  score: Nullable<number>;
  vpn: Nullable<string>;
  dns: Nullable<string[]>;
  dns_score: Nullable<number>;
  proxies: Nullable<string[]>;
  proxies_score: Nullable<number>;
  traffic_score: Nullable<number>;
  default_gateway_ip: Nullable<string[]>;
}

export interface TotalSeverityCount {
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface SeverityCount {
  date: string;
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface WindowsPendingUpdate {
  title: string;
  type: 'utDriver' | 'utSoftware';
  description: string;
  identity: string;
  is_mandatory: boolean;
  download_priority: 'dpLow' | 'dpNormal' | 'dpHigh' | 'dpExtraHigh';
  state: string;
  can_request_user_input: boolean;
  is_downloaded: boolean;
  is_installed: boolean;
  is_uninstallable: boolean;
  max_download_size: number | null;
  kb_article_ids: string[];
  categories: string[];
  security_bulletin_ids: string[];
  support_url: string;
}

export interface EnrichedWindowsPendingUpdate extends WindowsPendingUpdate {
  history: WindowsUpdateHistoryEntry[];
  latest_state_from_history?: WindowsUpdateHistoryStateEntry;
  syntheticBuildUpgrade?: { productVersion: string; targetVersion: string };
}

export interface OsInfo {
  version: string;
  patch: string;
  patch_type: 'beta' | 'dev';
  security_score: number;
  patched_vulnerabilities: number;
  unpatched_vulnerabilities: number;
  applied_patches: number;
  unapplied_patches: number;
  latest_available_patch: string;
  supported: boolean;
  // Windows specific
  architecture?: Nullable<string>;
  bitlocker?: Nullable<boolean>;
  boot_required?: Nullable<boolean>;
  current_build?: Nullable<string>;
  edition?: Nullable<string>;
  is_process_64_bit?: Nullable<boolean>;
  is_system_64_bit?: Nullable<boolean>;
  last_update_installation?: Nullable<string>;
  last_update_search?: Nullable<string>;
  pending_updates?: Nullable<WindowsPendingUpdate[]>;
  service_pack?: Nullable<string>;
  recommendations: DeviceRecommendation[];
}

export interface PlatformStats {
  devices_count: number;
  total_issues: number;
  avg_score: number;
}

export interface Proxy {
  hostname: string;
  port: number;
}

export interface RiskScore {
  date: Nullable<string>;
  global_score: Nullable<number>;
  application_score: Nullable<number>;
  os_score: Nullable<number>;
  settings_score: Nullable<number>;
}

export interface FirmIssues {
  android_issues: number;
  ios_issues: number;
}

export interface FirmDevices {
  scaned_devices: number;
  unupdated: number;
  rooted: number;
  count_android_devices: number;
  count_ios_devices: number;
  count_ipados_devices: number;
  count_macos_devices: number;
  count_windows_devices: number;
  count_linux_devices: number;
}

export interface SecurityEvaluation {
  app_version: string;
  data_version: string;
  invoked_by: ScanInvokedBy;
  os: OsInfo;
  device: DeviceScore;
  device_settings: DeviceSettings;
  network: NetworkInfo;
  applications: { security_score: Nullable<number> };
  global_score: GlobalScore;
  pending_updates: Nullable<AndroidPendingUpdates>;
  user: {
    email: string;
    id: string;
    name: string;
  };
}

export interface VerificationWithStatus {
  api_key: string;
  android_app_url: string;
  ios_app_url: string;
  base_url: string;
  status: Status;
  is_password_set: boolean;
  email: string;
}

export interface IssueSolution {
  platform: string;
  description: string;
  created: string;
}

export type IssueCategory = 'user' | 'location' | 'platform' | 'app';

// Codenames come from the backend check registry; FE branches on literals only where it must.
export type IssueCodename = string;

export type IssueObjectType = 'network' | 'device' | 'client' | 'email' | 'network_device' | 'user';

export interface Issue {
  title: string;
  description: string;
  codename: IssueCodename;
  object_type: IssueObjectType;
  score: Nullable<number>;
  category: IssueCategory;
  knowledge_base_url: string;
  n_affected_objects: number;
  n_ignored_objects: number;
  recommendations: Recommendation[];
}

export type RecommendationCodename = string;

export type RecommendationCategory =
  'android' | 'ios' | 'ipados' | 'macos' | 'windows' | 'linux' | 'network' | 'email' | 'user' | 'client';

export interface Recommendation {
  codename: RecommendationCodename;
  description: string;
  category: RecommendationCategory;
  resolvable: boolean;
  n_affected_objects: string;
}

export interface RecommendationDetail {
  codename: RecommendationCodename;
  description: string;
  category: RecommendationCategory;
  resolvable: boolean;
  affected_objects:
    DeviceUserEvaluation[] | NetworkScanWithoutUsers[] | UserData[] | Client[] | AffectedEmail[] | NetworkDevice[];
  ignored_objects: IgnoredObject<
    DeviceUserEvaluation | NetworkScanWithoutUsers | UserData | Client | AffectedEmail | NetworkDevice
  >[];
}

export interface IgnoredObject<T> {
  object: T;
  ignored_metadata: {
    ignored_at: string;
    ignored_until: Nullable<string>;
    ignored_note: Nullable<string>;
    ignored_by: Nullable<UserInfo>;
  };
}

export interface IssueDetail {
  title: string;
  description: string;
  codename: IssueCodename;
  object_type: IssueObjectType;
  score: Nullable<number>;
  category: IssueCategory;
  knowledge_base_url: string;
  affected_objects:
    DeviceUserEvaluation[] | NetworkScanWithoutUsers[] | UserData[] | Client[] | AffectedEmail[] | NetworkDevice[];
  ignored_objects: IgnoredObject<
    DeviceUserEvaluation | NetworkScanWithoutUsers | UserData | Client | AffectedEmail | NetworkDevice
  >[];
  recommendations: RecommendationDetail[];
}

export interface CVEIssue {
  score: number;
  description: string;
  published: string;
  cve: string;
  device?: string;
  solution: string;
  related_to: {
    os: boolean;
    vendor: boolean;
    user: boolean;
  };
  affected_devices: DeviceUserEvaluation[];
}

export interface ClientCount {
  count: number;
}

export interface Settings {
  language: 'en' | 'de';
}

export interface ErrorFields {
  code?: string;
  detail: string | { msg?: string }[];
  // A compliance refusal over clients: the ones the caller manages, and how many others.
  client_names?: string[];
  other_count?: number;
  // A refusal per object: each refused object with the code of its rule.
  objects?: RefusedObject[];
}

export interface RefusedObject {
  object_id: string;
  code: string;
}

export interface APIError {
  code?: number;
  code_name?: string;
  description?: string;
  client_names?: string[];
  other_count?: number;
  objects?: RefusedObject[];
}

export interface QRCodeAPIData {
  api_key: string;
  endpoint_url: string;
}

export interface EmailSendResponse {
  successfully_send: string[];
  unsuccessfully_send: { email: string; error: string }[];
}

export interface NotificationSender {
  id: string;
  email: string;
  name: string;
  position: string;
  status: 'active' | 'disabled' | 'pending';
  registered: string;
  role: Role;
}

export interface Notification {
  id: string;
  created: string;
  modified: Nullable<string>;
  title: Nullable<string>;
  message: Nullable<string>;
  notification_type: Nullable<string>;
  data: Nullable<Record<string, unknown>>;
  custom_data: Nullable<Record<string, unknown>>;
}

export enum NotificationType {
  CUSTOM_NOTIFICATION = 'custom-notification',
  BACKGROUND_SCAN = 'background-scan',
  BACKGROUND_SCAN_DONE = 'background-scan-done',
  PATCH = 'patch',
  VENDOR_UPDATES = 'vendor_updates',
  OS_END_OF_LIFE = 'os_end_of_life',
  DEVICE_END_OF_LIFE = 'device_end_of_life',
  ROOTED = 'rooted',
  JAILBROKEN = 'jailbroken',
  SCREEN_LOCK = 'screen_lock',
  BLUETOOTH = 'bluetooth',
  USB_DEBUG = 'usb_debug',
  EMAIL_SCAN_DONE = 'email-scan-done',
  NEW_SECURITY_PATCH = 'new-security-patch',
  GENERAL_RECOMMENDATION = 'general-recommendations',
  UNSCANNED_DEVICE = 'unscanned-device',
  SCHEDULED_RECOMMENDATION = 'scheduled-recommendations',
  SAFE_NETWORKS_CHANGED = 'safe-networks-changed',
  WIN_CMD_NETWORK_DEVICE_SCAN = 'win_cmd_network_device_scan',
  NEW_QUIZ_ASSIGNMENT = 'new-quiz-assignment'
}

export enum NotificationState {
  FAILED = 'failed',
  SEND = 'send',
  RECEIVED = 'received',
  READ = 'read'
}

export interface NotificationDevice {
  id: string;
  model: Nullable<string>;
  marketing_name: Nullable<string>;
  name: Nullable<string>;
  platform: Nullable<string>;
  manufacturer: Nullable<string>;
  owner: { id: string; email: string };
}

export interface SuccessfulNotificationResult {
  notification_log_id: string;
  notification_state: NotificationState;
  message_id: string;
  device: NotificationDevice;
}

export interface FailedNotificationResult {
  notification_log_id: string;
  notification_state: NotificationState;
  error: string;
  error_message: string;
  device: NotificationDevice;
}

export type NotificationSeverity = 'unknown' | 'safe' | 'warning' | 'risk';

export interface NotificationLog {
  batch_id: string;
  created: string;
  title: Nullable<string>;
  body: Nullable<string>;
  notification_type: NotificationType;
  severity: Nullable<NotificationSeverity>;
  sender: Nullable<{ id: string; email: string }>;
  successful_sends: SuccessfulNotificationResult[];
  failed_sends: FailedNotificationResult[];
}

export interface CellularNetwork {
  telecom_name: Nullable<string>;
  created: Nullable<string>;
  score: Nullable<number>;
  vpn: Nullable<boolean>;
  dns: Nullable<Dns[]>;
  dns_score: Nullable<number>;
  proxies: Nullable<Proxy[]>;
  proxies_score: Nullable<number>;
  traffic_score: Nullable<number>;
  recommendations: DeviceRecommendation[];
  network_settings_score: Nullable<number>;
}

export interface Network {
  vpn: Nullable<boolean>;
  score: number;
  network_security: Nullable<number>;
  network_connection_type: 'wifi' | 'cellular';
  created: string;
}

export interface WifiInfo {
  id: string;
  vpn: Nullable<boolean>;
  score: number;
  default_gateway_ip: Nullable<string[]>;
  dns: Nullable<string[]>;
  scan_created: string;
}

export interface WifiNetwork {
  ssid: Nullable<string>;
  score: Nullable<number>;
  created: Nullable<string>;
  vpn: Nullable<boolean>;
  dns: Nullable<Dns[]>;
  dns_score: Nullable<number>;
  proxies: Nullable<Proxy[]>;
  proxies_score: Nullable<number>;
  traffic_score: Nullable<number>;
  network_security: Nullable<string>;
  network_security_score: Nullable<number>;
  recommendations: DeviceRecommendation[];
  network_settings_score: Nullable<number>;
}

export interface UnidentifiedWifi {
  score: number;
  created: string;
  vpn: Nullable<boolean>;
  dns: Nullable<Dns[]>;
  dns_score: Nullable<number>;
  proxies: Nullable<Proxy[]>;
  proxies_score: Nullable<number>;
  default_gateway_ip: Nullable<string[]>;
  network_security: Nullable<string>;
  network_security_score: Nullable<number>;
}

export interface EthernetInfo {
  id: string;
  score: number;
  vpn: Nullable<boolean>;
  default_gateway_ip: Nullable<string[]>;
  dns: Nullable<string[]>;
  proxies: Nullable<string[]>;
  scan_created: string;
}

export interface EthernetNetwork {
  ssid: Nullable<string>;
  created: Nullable<string>;
  score: Nullable<number>;
  vpn: Nullable<boolean>;
  dns: Nullable<Dns[]>;
  dns_score: Nullable<number>;
  proxies: Nullable<Proxy[]>;
  proxies_score: Nullable<number>;
  traffic_score: Nullable<number>;
  recommendations: DeviceRecommendation[];
  network_settings_score: Nullable<number>;
}

export type NetworkType = 'wifi' | 'cellular' | 'ethernet' | 'unknown';

// eslint-disable-next-line max-len
export type ClientNetworkType =
  'third_party_network' | 'office_network' | 'remote_office_network' | 'home_office_network';

export interface NetworkUser {
  id: string;
  email: string;
  name: string;
  last_scan_done: string;
  number_of_devices: number;
  devices: DeviceEvaluation[];
}

export type NetworkProtocol = 'TCP' | 'UDP' | 'TCP/UDP';

export interface FirewallCheck {
  check: any;
  port: number;
  protocol: NetworkProtocol;
  name: string;
  type: string;
  severity: 'high' | 'medium' | 'low' | 'unknown';
  blocked: boolean;
}

export interface PublicIP {
  ipv4: Nullable<string>;
  ipv6: Nullable<string>;
}

export interface NetworkScan {
  id: Nullable<string>;
  name: Nullable<string>;
  custom_name: Nullable<string>;
  discovered_name: Nullable<string>;
  last_scan_done: string;
  network_type: NetworkType;
  client_type: ClientNetworkType;
  score: Nullable<number>;
  vpn: Nullable<string>;
  dns: Nullable<string[]>;
  dns_score: Nullable<number>;
  proxies: Nullable<string[]>;
  proxies_score: Nullable<number>;
  traffic_score: Nullable<number>;
  firewall_score: Nullable<number>;
  firewall_check: Nullable<FirewallCheck[]>;
  default_gateway_ip: Nullable<string[]>;
  network_settings_score: Nullable<number>;
  recommendations: DeviceRecommendation[];
  public_ip: Nullable<PublicIP>;
  user_count: number;
  external_scan_approval: Nullable<'granted' | 'rejected'>;
  is_safe: boolean;
  last_safescan: Nullable<string>;
  last_external_scan: Nullable<string>;
  /** The office target's last Shodan attempt; sent on the client network list only. */
  external_scan_attempt?: Nullable<ShodanAttempt>;
  /** Set on the client's office network reads when a target group scans its current public address. */
  target_group_link?: Nullable<TargetGroupLink>;
}

export interface NetworkScanWithoutUsers {
  id: Nullable<string>;
  name: Nullable<string>;
  custom_name: Nullable<string>;
  discovered_name: Nullable<string>;
  last_scan_done: string;
  network_type: NetworkType;
  client_type: ClientNetworkType;
  score: Nullable<number>;
  vpn: Nullable<string>;
  dns: Nullable<string[]>;
  dns_score: Nullable<number>;
  proxies: Nullable<string[]>;
  proxies_score: Nullable<number>;
  traffic_score: Nullable<number>;
  default_gateway_ip: Nullable<string[]>;
  network_settings_score: Nullable<number>;
  recommendations: DeviceRecommendation[];
  public_ip: PublicIP;
  is_safe: boolean;
}

export interface PrivacyInfo {
  security_score: number;
  email_scans: EmailScan[];
  recommendations: DeviceRecommendation[];
}

export interface UserEmailScan {
  id: string;
  email: string;
  scan_requested: string;
  scan_done: Nullable<string>;
  breaches: EmailBreach[];
  score: Nullable<number>;
  data_classes: Nullable<string[]>;
  top_secret_data_classes: Nullable<string[]>;
  secret_data_classes: Nullable<string[]>;
  confidential_data_classes: Nullable<string[]>;
  sensitive_data_classes: Nullable<string[]>;
  restricted_data_classes: Nullable<string[]>;
  protected_data_classes: Nullable<string[]>;
  not_categorized_data_classes: Nullable<string[]>;
}

export interface EmailScan extends UserEmailScan {
  users: {
    id: string;
    name: string;
    email: string;
  }[];
}

export interface AffectedEmail {
  id: string;
  email: string;
  scan_requested: string; // date-time
  scan_done: Nullable<string>; // date-time
  n_breaches: number;
  users: {
    id: string;
    name: string;
    email: string;
  }[];
}

export interface ProcessingEmailScan {
  id: string;
  email: string;
  scan_requested: string;
}

export interface EmailBreach {
  id: string;
  name: string;
  title: string;
  domain: string;
  breach_date: string;
  added_date: string;
  modified_date: string;
  pwn_count: number;
  description: string;
  data_classes: string[];
  top_secret_data_classes: string[];
  secret_data_classes: string[];
  classified_data_classes: string[];
  confidential_data_classes: string[];
  sensitive_data_classes: string[];
  restricted_data_classes: string[];
  protected_data_classes: string[];
  not_categorized_data_classes: string[];
  is_verified: boolean;
  is_fabricated: boolean;
  is_sensitive: boolean;
  is_retired: boolean;
  is_spam_list: boolean;
  logo_path: string;
  status: 'active' | 'resolved';
}

export interface EmailBreachScan {
  breaches: EmailBreach[];
  security_score: number;
  finished_email_scans: EmailScan[];
  processing_email_scans: ProcessingEmailScan[];
}

export interface UserEmailBreachScan {
  security_score: number;
  finished_email_scans: UserEmailScan[];
  processing_email_scans: ProcessingEmailScan[];
}

export interface AppScanDetection {
  /** Total */
  total: number;
  /** Detected */
  detected: number;
}

export interface AppScanPermission {
  id?: number;
  name: string;
  status: string;
  info?: string;
  description?: string;
}

export interface SimpleAppScan {
  package_name: string;
  app_name: string;
  app_version: string;
  avg_cvss?: number;
  security_score?: number;
  trackers_detection?: AppScanDetection;
  virus_detection?: AppScanDetection;
  /** Permissions */
  permissions?: AppScanPermission[];
}

export interface UserImportError {
  key: string;
  message: string;
}

export interface UserInfo {
  id: Nullable<string>;
  name: string;
  email: string;
}

export interface ImportUser {
  id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  // role: 'user' | 'administrator',
  role: string;
  email: string;
  position?: string;
  country: string;
  language: string;
  department_id?: Nullable<string>;
  errors?: UserImportError[];
}

export interface ImportUserForExport {
  username: string;
  first_name?: string;
  last_name?: string;
  // 'user' = 1 | 'administrator' = 2 | 'firmadmin' = 3,
  role: number;
  email: string;
  position?: string;
  language: string;
  country: string;
  department_id?: Nullable<string>;
  errors?: UserImportError[];
}

export interface PartnerAdminImport {
  email: string;
  username: Nullable<string>;
  position: Nullable<string>;
}

export interface DeviceRecommendation {
  group: string;
  group_codename: string;
  title: string;
  codename: string;
  issue_description: string;
  issue_category: 'user' | 'location' | 'platform' | 'app';
  issue_codename: IssueCodename;
  recommendation_description: string;
  recommendation_category: Platforms | 'network' | 'email';
  recommendation_type: string;
  resolvable: boolean;
  score: number;
  severity: 'unknown' | 'safe' | 'warning' | 'risk';
  knowledge_base_url: string;
  mapped_object_id: Nullable<string>;
}

export interface DeviceRecommendations {
  recommendations: DeviceRecommendation[];
  ignored_recommendations: IgnoredObject<DeviceRecommendation>[];
}

export interface AndroidAppIcons {
  package_icons: Record<string, Nullable<string>>;
}

export interface QuizChoice {
  text: string;
  is_correct: boolean;
}

export type QuestionType = 'single_correct' | 'multiple_correct';

export interface QuizQuestion {
  text: string;
  takeaway: string;
  question_type: QuestionType;
  choices: QuizChoice[];
  max_score: number;
}

export interface CreateQuiz {
  title: string;
  description: string;
  questions: QuizQuestion[];
  keywords: string[];
}

export interface QuizBase {
  quiz_version_id: string;
  quiz_series_id: string;
  version_num: number;
  created: string;
  max_score: number;
  include_in_notifications?: boolean;
}

export type Quiz = QuizBase & CreateQuiz;

export interface QuizVersion extends QuizBase {
  i18n: Partial<Record<'en' | 'cs' | 'de', CreateQuiz>>;
}

export type QuizAssignmentStatus = 'assigned' | 'in_progress' | 'completed';

export interface AssignedUser {
  user: {
    id: string;
    name: string;
    email: string;
  };
  status: QuizAssignmentStatus;
  answers: QuizAnswer[];
  score: number;
  completion_date: Nullable<string>;
  success_rate: number;
}

export interface QuizAssignment {
  id: string;
  created: string;
  quiz: Quiz;
  assigned_users: AssignedUser[];
}

export interface QuizResendResult {
  sent: number;
  skipped: number;
}

export interface QuizAnswer {
  question_indx: number;
  /** A one-correct question's answer; a multiple-correct one carries `choice_indices` instead. */
  choice_indx?: number;
  choice_indices?: number[];
  answer_score: number;
  is_answer_correct: boolean;
  spent_time_ms: number;
}

export interface UserQuizAssignment {
  user: {
    id: string;
    name: string;
    email: string;
  };
  quiz: Quiz;
  status: QuizAssignmentStatus;
  answers: QuizAnswer[];
  completion_date: Nullable<string>;
  score: number;
  assignment_token: string;
  quiz_assignment_id: string;
  success_rate: Nullable<number>;
  created: string;
}

export interface GoogleWorkspaceUser {
  email: string;
  is_admin: boolean;
  is_suspended: boolean;
  is_archived: boolean;
}

export interface IssueIgnoreCodename {
  codename: string;
  object_ids: string[] | 'all';
}

export interface IAddress {
  country: string;
  street: string;
  city: string;
  zip_code: string;
  state: string;
}

export const CustomComplianceObjectTypes = ['device', 'email', 'user', 'client', 'network', 'network_device'] as const;

export const CustomComplianceActionsWithSelectedUsers = [
  'send_email_to_selected_users',
  'send_notification_to_selected_users'
] as const;

export const CustomComplianceActionsWithRestartOptions = ['run_cmd_restart_affected_device'] as const;

export const CustomComplianceActionsWithoutData = [
  'send_email_to_admins',
  'send_email_to_affected_user',
  'send_notification_to_admins',
  'send_notification_to_affected_user'
] as const;

export type CustomComplianceAction =
  | (typeof CustomComplianceActionsWithSelectedUsers)[number]
  | (typeof CustomComplianceActionsWithoutData)[number]
  | (typeof CustomComplianceActionsWithRestartOptions)[number];

export const ComplianceSeverities = ['critical', 'high', 'medium', 'low', 'info'] as const;

export type ComplianceSeverity = (typeof ComplianceSeverities)[number];

export interface CustomComplianceGeneralRule {
  recommendation_codename: string;
  check_codename: string;
  check_name: string;
  outcome_codename: string;
  outcome_label: string;
  rule_description_i18n: string;
  default_band: ComplianceSeverity;
  object_type: (typeof CustomComplianceObjectTypes)[number];
  // Null for a key that serves every device platform and for non-device checks.
  platform: Nullable<Platforms>;
  possible_actions: CustomComplianceAction[];
}

export interface CustomComplianceRuleSelectedAction {
  withoutData: {
    action: (typeof CustomComplianceActionsWithoutData)[number];
  };
  withSelectedUsers: {
    action: (typeof CustomComplianceActionsWithSelectedUsers)[number];
    selected_users: string[];
  };
  /** A rule stored before the restart options has no mode and no delay: the backend reads it as soft, 10 minutes. */
  withRestartOptions: {
    action: (typeof CustomComplianceActionsWithRestartOptions)[number];
    mode?: RebootMethod;
    delay_minutes?: RestartDelayMinutes;
  };
}

export interface CustomComplianceRule {
  recommendation_codename: string; // "uid"
  selected_actions: (
    | CustomComplianceRuleSelectedAction['withoutData']
    | CustomComplianceRuleSelectedAction['withSelectedUsers']
    | CustomComplianceRuleSelectedAction['withRestartOptions']
  )[];
  band: ComplianceSeverity;
}

export interface CustomComplianceConfig {
  name: string;
  description: string;
  version: string;
  data: CustomComplianceRule[];
}

// A copy of a compliance; BE trims the name and refuses a blank one.
export interface CustomComplianceCopy {
  name: string;
  description: string;
}

export interface CustomComplianceClientCopy extends CustomComplianceCopy {
  make_default: boolean;
}

export type CustomComplianceRuleObjectType = 'device' | 'email' | 'user' | 'client' | 'network' | 'network_device';

export type CustomComplianceOwnerKind = 'platform' | 'firm' | 'client';

export interface CustomComplianceConfigResponse extends CustomComplianceConfig {
  compliance_id: string;
  // Set only on a platform compliance, e.g. the Security baseline.
  code: Nullable<string>;
  owner_kind: CustomComplianceOwnerKind;
  owner_name: Nullable<string>;
  can_edit: boolean;
  // A default compliance needs one; one whose active checks are all Info has it.
  has_active_check: boolean;
  created: string;
  modified: string;
}

export interface CustomComplianceClientConfigResponse extends CustomComplianceConfigResponse {
  is_currently_active: boolean;
  // The client's default compliance, exactly one per client.
  is_default: boolean;
  active_compliance_stats: {
    n_compliance_rules: number;
    n_broken_compliance_rules: number;
  } | null;
}

// Of the objects a check evaluates (`population`), those failing it today; inactive checks included.
export interface CustomComplianceCheckCount {
  check_codename: string;
  failing: number;
  population: number;
}

export interface CustomComplianceClient {
  id: string;
  name: string;
  status: Status;
  is_approved: true;
  contact_name: string;
  contact_email: string;
}

export interface CustomComplianceFirmConfigResponse extends CustomComplianceConfigResponse {
  assigned_clients: {
    id: string;
    name: string;
    status: ClientStatus;
    is_approved: boolean;
    contact_name: string;
    contact_email: string;
    // Whether this compliance is that client's default.
    is_default: boolean;
  }[];
  // The default of the customers the firm creates as managed by it.
  is_default_for_new_clients: boolean;
}

export type Timespan = 'last-week' | 'last-month' | 'last-quarter' | 'last-year' | 'this-year';

export interface TokenExchange {
  hostname: string;
  success: boolean;
  error_message: Nullable<{ error_msg: string; device_uuid: string }>;
  device: DeviceEvaluation;
  created: string;
}

export interface HeadlessSecretTokenLog {
  token_exchanges: TokenExchange[];
}

export interface HeadlessSecretTokenInfo {
  headless_token_id: string;
  created: string;
  requested_by: UserInfo | string;
  headless_user: {
    id: string;
    email: string;
    name: string;
    role: Role;
  };
  expiration_date: string;
  secret_token: string;
  device_stats: {
    accepted: number;
    pending: number;
    rejected: number;
    onboarded: number;
  };
}

export type HeadlessSecretTokenDeviceStatus = 'accepted' | 'pending' | 'rejected' | 'onboarded';

export interface HeadlessSecretTokenDevice {
  device_uuid: string;
  hostname: string;
  local_ip_address: string;
  status: HeadlessSecretTokenDeviceStatus;
  resolved_by: Nullable<UserInfo | string>;
  resolved_at: Nullable<string>;
  resolved_as: Nullable<'accepted' | 'rejected'>;
  onboarded_at: Nullable<string>;
  // ISO 8601 format date string
  first_request_at: Nullable<string>;
  // ISO 8601 format date string
  last_request_at: Nullable<string>;
  device: DeviceEvaluation;
}

export interface HeadlessSecretTokenDetails {
  headless_token_id: string;
  created: string;
  requested_by: UserInfo | string;
  headless_user: {
    id: string;
    email: string;
    name: string;
    role: Role;
  };
  expiration_date: string;
  secret_token: string;
  devices: HeadlessSecretTokenDevice[];
}

export interface PartnerDevices {
  client_id: string;
  client_name: string;
  devices: DeviceUserEvaluation[];
}

export interface PartnerNetworks {
  client_id: string;
  client_name: string;
  network_scans: NetworkScan[];
}

export interface PartnerPrivacyEmails {
  client_id: string;
  client_name: string;
  security_score: number;
  finished_email_scans: EmailScan[];
  processing_email_scans: ProcessingEmailScan[];
}

export interface DomainValidity {
  domain: string;
  port: number;
}

export interface DomainValidityResult {
  domain_name: string;
  port: number;
  valid_from: string;
  valid_to: string;
  modified: string;
  issuer: string;
  is_self_signed: boolean;
}

export interface CreatePhishingCampaign {
  name: string;
  target_users: string[];
  bait_id: string;
  launch_date: string | null; // if null, launch immediately
  send_by_date: string | null; // if null, send all emails as soon as possible (after `launch_date` if set)
}

export type PhishingCampaignStatus = 'In progress' | 'Queued' | 'Created' | 'Emails Sent' | 'Completed';

export type PhishingCampaignEventStatus =
  | 'Email Sent'
  | 'Error Sending Email'
  | 'Email Opened'
  | 'Clicked Link'
  | 'Submitted Data'
  | 'Email Reported'
  | 'Proxied request'
  | 'Campaign Created'
  | 'Campaign Completed';

export type PhishingUserStatus = 'Success' | 'Queued' | 'Sending' | 'Unknown' | 'Scheduled' | 'Retrying' | 'Error';

/**
 * The three status unions above list every value the backend is specified to send. They are kept
 * as unions (rather than widened to `string`) so a typo is a compile error and `switch`es stay
 * exhaustive; an unrecognised value arriving at runtime is not a type error but a presentation
 * one, and `usePhishingStatus` already renders it as a neutral placeholder.
 */

/**
 * GoPhish-style phishing library (backend refactor).
 *
 * The legacy flat types (PhishingBait, PhishingCampaignResponse, PhishingCampaignDetail,
 * PhishingCampaignUserResult, PhishingCampaignEvent) were retired in Stage 2.1 once every
 * consumer flipped to the nested shapes below. `PhishingCampaignUserResultWithTimeline` is
 * kept but re-based on `GophishCampaignUserResult`.
 */

// --- Shared sub-resources ---------------------------------------------------

/**
 * Templates and landing pages are versioned series: `id` is the series id, content lives in
 * an immutable version row. A campaign freezes the version it launched with, so editing a
 * referenced series forks `version_num + 1` instead of mutating in place. `is_editable` says
 * whether the current version is still unreferenced (and so editable in place); it is
 * `null`/absent when the details are embedded in a bait, where the flag has no meaning.
 */
export interface PhishingTemplateDetails {
  id: string;
  name: string;
  subject: string;
  text: string;
  html: string;
  version_num: number;
  version_id: string;
  is_editable?: boolean | null;
  modified_date?: string | null;
  owned_by_client: boolean;
}

export interface PhishingLandingPageDetails {
  id: string;
  name: string;
  html: string;
  redirect_url: string | null; // key always present, value nullable
  version_num: number;
  version_id: string;
  is_editable?: boolean | null;
  modified_date?: string | null;
  owned_by_client: boolean;
}

// Client-facing SMTP projection: only id + from_address are ever exposed.
export interface SendingProfileFromAddress {
  id: string;
  from_address: string;
}

export interface PhishingBaitDetails {
  id: string;
  name: string;
  created: string;
  is_active: boolean;
  owned_by_client?: string | null; // uuid = client-owned/editable; null = built-in/read-only
  template: PhishingTemplateDetails;
  page: PhishingLandingPageDetails;
  smtp: SendingProfileFromAddress;
}

// --- Listing types ----------------------------------------------------------

/**
 * Row shape shared by the two versioned library resources (templates, landing pages). Their
 * listings are structurally identical - only the details differ - which is what lets one
 * component render both tabs.
 */
export interface PhishingLibraryResourceListing {
  id: string; // series id, see PhishingTemplateDetails / PhishingLandingPageDetails
  name: string;
  created: string;
  owned_by_client: boolean;
  version_num: number;
  is_editable: boolean;
}

/**
 * Aliases, not distinct interfaces: the two listings are identical today, and the project's
 * `no-empty-object-type` rule (correctly) rejects an empty `extends` as equivalent to its supertype.
 * Give whichever one diverges its own interface at the point it actually gains a field.
 */
export type PhishingTemplateListing = PhishingLibraryResourceListing;

export type PhishingLandingPageListing = PhishingLibraryResourceListing;

export interface SendingProfileClientListing {
  id: string;
  from_address: string;
  created: string;
  owned_by_client: boolean;
}

// --- Platform-only types ----------------------------------------------------

export interface SendingProfileListing {
  // platform: exposes internal name
  id: string;
  name: string;
  created: string;
  owned_by_client: boolean;
}

export interface SendingProfileDetails {
  // NOTE: password is never returned by the API
  id: string;
  name: string;
  username: string | null;
  host: string;
  interface_type: string;
  from_address: string;
  ignore_cert_errors: boolean;
  url: string;
  modified_date?: string | null;
}

export interface PhishingUrlListing {
  id: string;
  url: string;
  created: string;
}

// --- Request bodies ---------------------------------------------------------

export interface PhishingBaitNewRequest {
  name: string;
  template: string;
  page: string;
  smtp: string;
  is_active?: boolean; // default true
}

export interface GophishTemplateRequest {
  name: string;
  subject: string;
  text: string;
  html: string;
}

export interface GophishPageRequest {
  name: string;
  html: string;
  redirect_url?: string | null;
}

export interface GophishSendingProfileRequest {
  name: string;
  username?: string | null;
  password?: string | null; // superadmin types it; never prefilled
  host: string;
  interface_type?: string; // default 'SMTP'
  from_address: string;
  ignore_cert_errors?: boolean; // default false
  headers?: { key: string; value: string }[] | null;
  url: string; // uuid, required
}

export interface PhishingUrlNewRequest {
  url: string;
}

export interface PhishingCampaignTestRequest {
  target_users: string[]; // min 1, max 5 uuids
  bait_id: string;
}

// --- Campaign listing + detail (replace legacy in Stage 2.1) ----------------

export interface PhishingCampaignListing {
  id: string;
  name: string;
  created: string;
  launch_date?: string | null; // was non-null
  status: PhishingCampaignStatus;
  completed: boolean;
  owned_by_client?: string | null;
}

export interface PhishingTargetUser {
  user_id: string;
  first_name: string | null; // nullable on target users
  last_name: string | null; // nullable on target users
  email: string;
}

export interface GophishCampaignUserResult {
  // names NON-null here (unlike PhishingTargetUser)
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  status: PhishingUserStatus;
  ip?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  send_date: string;
  reported: boolean;
}

export interface PhishingCampaignUserResultWithTimeline extends GophishCampaignUserResult {
  timeline: GophishCampaignEvent[];
}

export interface GophishCampaignEvent {
  email: string;
  time: string;
  message: PhishingCampaignEventStatus;
}

export interface GophishCampaignResults {
  name: string;
  status: PhishingCampaignStatus;
  results: GophishCampaignUserResult[];
  timeline: GophishCampaignEvent[];
}

export interface GophishCampaignStats {
  total: number;
  sent: number;
  opened: number;
  clicked: number;
  submitted_data: number;
  email_reported: number;
  error: number;
}

export interface GophishCampaignSummary {
  id: number;
  name: string;
  status: PhishingCampaignStatus;
  created_date: string;
  launch_date: string;
  send_by_date: string;
  completed_date: string;
  stats: GophishCampaignStats;
}

export interface PhishingCampaignDetails {
  name: string;
  bait: PhishingBaitDetails; // nested
  target_users: PhishingTargetUser[];
  launch_date: string | null;
  send_by_date: string | null;
  results: GophishCampaignResults | null; // NULLABLE
  summary: GophishCampaignSummary | null; // NULLABLE
  completed: boolean; // NEW
  archived: boolean; // NEW
}

export type RebootMethod = 'soft' | 'hard';

/** The restart delays the console offers, in minutes. */
export type RestartDelayMinutes = 1 | 10 | 30 | 60;

/**
 * What the restart controls hold: one shape for the reboot modal and the compliance actions drawer.
 * The delay is in minutes; callers convert at the edge they send to.
 */
export interface RestartOptions {
  mode: RebootMethod;
  delayMinutes: RestartDelayMinutes;
}

/**
 * Scope of a `win-cmd-cancel-updates` command. It must always be sent explicitly: an omitted
 * scope means `all` on the agent, and an unrecognised one is a 422 by design.
 * `resume` starts installing, so it carries the same weight as a cancel.
 */
export type WindowsCancelUpdatesScope = 'job' | 'all' | 'pause' | 'resume';

export interface CPE {
  original: string;
  type: string;
  vendor: string;
  product: string;
  version: string;
}

export interface SafescanStatus {
  scan_id: string;
  device_id: string;
  scan_created_timestamp?: string;
  scan_start_timestamp?: string;
  scan_end_timestamp?: string;
  progress?: number;
  network: string;
  scan_type: 'fast' | 'full';
  report_available: boolean;
  stop_reason?: 'user_requested' | 'websocket_timeout' | 'websocket_did_not_connect' | 'network_mismatch';
  archived: boolean;
  hosts_overview: SafescanStatusHostOverview[];
  status: SafescanStatusStatus;
}

export enum SafescanStatusStatus {
  NEW = 'new',
  RUNNING = 'running',
  CANCELLED = 'cancelled',
  FINISHED = 'finished',
  UNKNOWN = 'unknown'
}

export interface SafescanStatusHostOverview {
  ip: string;
  hostname: Nullable<string>;
  ports: {
    port: number;
    protocol: string;
    detection_count: number;
    max_cvss: number;
    max_severity: string;
  }[];
  total_detection_count: number;
  total_max_cvss: number;
  total_max_severity: string;
}
export interface SafescanStatusHostPort {
  port: number;
  protocol: string;
  detection_count: number;
  max_cvss: number;
  max_severity: string;
}

export type SafescanReport = SafescanPDFReport | SafescanJSONReport;

export interface SafescanPDFReport {
  type: 'pdf';
  encoding: Nullable<string>;
  content: string;
}

export interface SafescanJSONReport {
  type: 'json';
  encoding: Nullable<string>;
  content: SafescanReportHost[];
}

export interface SafescanReportHost {
  ip: string;
  hostname: string | null;
  ports: SafeScanReportPort[];
  total_detection_count: number;
  total_max_cvss: number; // Highest score, 1-10
  total_max_severity: string; // Highest severity score, "Critical", "High", "Medium", "Low"
  host_only_detections: SafeScanReportDetection[]; // Vulnerabilities found at the host level not tied to a specific port
  detected_platforms?: string[];
  normalized_platforms?: string[];
  detected_services?: string[];
}
export interface SafeScanReportPort {
  port: number;
  protocol: 'tcp' | 'udp';
  detection_count: number; // Number of vulnerability detections found on this specific port
  max_cvss: number; // Highest CVSS score (1-10) among detections on this port
  max_severity: string; // Highest severity score ("Critical", "High", "Medium", "Low") among detections on this port
  detections: SafeScanReportDetection[]; // Details of each vulnerability found on this port
  detected_services?: string[];
}
export interface SafeScanReportDetection {
  qod: number; // Quality of Detection value indicating reliability of this detection (least reliable 0-100 most reliable)
  cvss: number; // severity CVSS score, 1-10
  severity: string; // severity score, "Critical", "High", "Medium", "Low"
  nvt_name: string; // Name of the vulnerability
  summary: string; // Brief summary of the vulnerability
  specific_result: string; // Summary of the vulnerability
  impact: string; // What could happen if the vulnerability is exploited
  solution: string; // Recommended solution or mitigation to fix or reduce the vulnerability risk
  cves: string; // Associated CVEs related to this vulnerability
  other_references: string;
  affected_software: string;
  vulnerability_insight: string; // Simplified summary
  detected_cpe?: string[];
}

export * from 'types/helpers';
